/**
 * The polling loop.
 *
 * An ELM327 is strictly one request at a time, and each round trip costs
 * 20-100 ms depending on the clone and the bus. That budget is the whole
 * problem: you cannot poll thirty signals at 20 Hz, so the scheduler decides
 * what to ask for next.
 *
 * Two things make it fast:
 *
 *   Batching  -- CAN vehicles accept up to six PIDs in one request and answer
 *                them in one frame, turning six round trips into one.
 *   Fair share -- requests are ordered by how overdue they are relative to
 *                their own interval, so a 30 s signal never delays a 50 ms one
 *                but also never starves.
 */
import { ObdError } from './parse.js';
import { PID_BY_NUMBER } from '../pids/registry.js';

export interface PidRequester {
  /** Issue one request. Resolves with whatever came back. */
  requestPids(pids: number[]): Promise<{ values: Map<number, Uint8Array>; missing: number[] }>;
  readonly canBatch: boolean;
  readonly maxBatchSize: number;
  /**
   * Whether these PIDs can be asked for in a single request. Lets the
   * requester veto a batch whose reply would be too large for the adapter.
   */
  responseFitsOneRequest(pids: number[]): boolean;
}

interface Subscription {
  pid: number;
  intervalMs: number;
  nextDue: number;
  consecutiveMisses: number;
  /** Timeouts in a row on a request for this PID and nothing else. */
  consecutiveTimeouts: number;
  /** Set when the ECU has proven it will not answer for this PID. */
  suspended: boolean;
}

export interface SchedulerStats {
  /** Completed adapter round trips per second. */
  requestsPerSec: number;
  /** Individual PID values decoded per second. */
  valuesPerSec: number;
  /** Smoothed round-trip latency in milliseconds. */
  latencyMs: number;
  /** Fraction of requests that returned nothing useful, 0..1. */
  errorRate: number;
  activePids: number;
  batching: boolean;
}

export interface SchedulerOptions {
  /** Invoked after each batch so derived values can be recomputed once. */
  onBatch?: (updated: number[]) => void;
  onError?: (err: Error) => void;
  /** Misses before a PID is assumed unsupported and dropped. */
  missesBeforeSuspend?: number;
  /**
   * Timeouts on a request for one PID alone before it is dropped.
   *
   * Higher than the miss threshold on purpose. An explicit "NO DATA" is the
   * car declining, which is good evidence; a timeout is the adapter saying
   * nothing at all, which could equally be a glitchy link.
   */
  timeoutsBeforeSuspend?: number;
}

export class PollScheduler {
  private readonly subs = new Map<number, Subscription>();
  private running = false;
  private loopHandle: Promise<void> | null = null;

  private reqCount = 0;
  private valCount = 0;
  private errCount = 0;
  private latency = 0;
  private windowStart = 0;
  private stats: SchedulerStats = {
    requestsPerSec: 0, valuesPerSec: 0, latencyMs: 0, errorRate: 0, activePids: 0, batching: false,
  };

  private readonly missesBeforeSuspend: number;
  private readonly timeoutsBeforeSuspend: number;
  /**
   * Working ceiling on batch size, lowered when requests fail outright.
   *
   * Adapters vary in ways no probe fully covers, so rather than trusting the
   * declared maximum for ever, a failed multi-PID request halves this and a run
   * of successes walks it back up. A clone that cannot cope settles at one PID
   * per request and keeps working instead of timing out for ever.
   */
  private batchLimit: number;

  constructor(
    private readonly requester: PidRequester,
    private readonly onValues: (pid: number, data: Uint8Array) => void,
    private readonly opts: SchedulerOptions = {},
  ) {
    this.missesBeforeSuspend = opts.missesBeforeSuspend ?? 4;
    this.timeoutsBeforeSuspend = opts.timeoutsBeforeSuspend ?? 8;
    this.batchLimit = Math.max(1, requester.maxBatchSize);
  }

  /** Current self-tuned batch ceiling, for diagnostics. */
  get currentBatchLimit(): number {
    return this.batchLimit;
  }

  get currentStats(): Readonly<SchedulerStats> {
    return this.stats;
  }

  /** Add a PID to the rotation, or change how often an existing one is read. */
  subscribe(pid: number, intervalMs?: number): void {
    const def = PID_BY_NUMBER.get(pid);
    const interval = intervalMs ?? def?.defaultIntervalMs ?? 1000;
    const existing = this.subs.get(pid);
    if (existing) {
      existing.intervalMs = interval;
      // Re-arm immediately so a faster rate takes effect without waiting out
      // the old, longer interval.
      existing.nextDue = Math.min(existing.nextDue, Date.now() + interval);
      existing.suspended = false;
      existing.consecutiveMisses = 0;
      existing.consecutiveTimeouts = 0;
      return;
    }
    this.subs.set(pid, {
      pid, intervalMs: interval, nextDue: 0, consecutiveMisses: 0, consecutiveTimeouts: 0, suspended: false,
    });
  }

  unsubscribe(pid: number): void {
    this.subs.delete(pid);
  }

  clear(): void {
    this.subs.clear();
  }

  /** PIDs currently in rotation, excluding ones proven unsupported. */
  activePids(): number[] {
    return [...this.subs.values()].filter((s) => !s.suspended).map((s) => s.pid);
  }

  isSuspended(pid: number): boolean {
    return this.subs.get(pid)?.suspended ?? false;
  }

  intervalOf(pid: number): number | undefined {
    return this.subs.get(pid)?.intervalMs;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.windowStart = Date.now();
    this.loopHandle = this.loop();
  }

  async stop(): Promise<void> {
    this.running = false;
    try {
      await this.loopHandle;
    } catch {
      /* the loop swallows its own errors; this is just to await settling */
    }
    this.loopHandle = null;
  }

  private async loop(): Promise<void> {
    while (this.running) {
      const now = Date.now();
      const batch = this.selectBatch(now);

      if (batch.length === 0) {
        await sleep(this.msUntilNextDue(now));
        continue;
      }

      const started = Date.now();
      try {
        const { values, missing } = await this.requester.requestPids(batch);
        const elapsed = Date.now() - started;
        this.latency = this.latency === 0 ? elapsed : this.latency * 0.8 + elapsed * 0.2;
        this.reqCount++;

        const updated: number[] = [];
        for (const [pid, data] of values) {
          const sub = this.subs.get(pid);
          if (sub) {
            sub.consecutiveMisses = 0;
            sub.consecutiveTimeouts = 0;
          }
          this.onValues(pid, data);
          this.valCount++;
          updated.push(pid);
        }
        for (const pid of missing) this.noteMiss(pid);
        if (missing.length === batch.length) this.errCount++;

        // Reschedule from completion, not from when it was due: on a slow bus
        // that keeps the loop honest instead of building an ever-growing debt.
        const done = Date.now();
        for (const pid of batch) {
          const sub = this.subs.get(pid);
          if (sub) sub.nextDue = done + sub.intervalMs;
        }

        // A run of clean multi-PID requests earns back some batch size.
        if (batch.length >= this.batchLimit && this.batchLimit < this.requester.maxBatchSize) {
          this.batchLimit++;
        }

        this.opts.onBatch?.(updated);
      } catch (err) {
        this.errCount++;
        // A batched request that fails outright is far more likely to be the
        // batch than the PIDs, so shrink before blaming anything else. Once the
        // limit reaches one the blame is no longer ambiguous: a PID asked for
        // on its own that keeps timing out is one the ECU will not answer, and
        // retrying it for ever costs a round trip every time. That case used to
        // be invisible, because only a reply the adapter did return could be
        // counted as a miss and a timeout returns nothing at all.
        if (batch.length > 1) this.batchLimit = Math.max(1, Math.floor(batch.length / 2));
        else if (err instanceof ObdError && err.kind === 'timeout') this.noteTimeout(batch[0]);
        for (const pid of batch) {
          const sub = this.subs.get(pid);
          // A transport-level failure is not the PID's fault, so only back off.
          if (sub) sub.nextDue = Date.now() + Math.min(sub.intervalMs * 2, 2000);
        }
        this.opts.onError?.(err instanceof Error ? err : new Error(String(err)));
        await sleep(50);
      }

      this.refreshStats();
    }
  }

  /**
   * Pick the next request.
   *
   * Ordering is by lateness expressed as a multiple of the PID's own interval,
   * so "50 ms signal that is 60 ms late" outranks "30 s signal that is 1 s
   * late" without any explicit priority levels.
   */
  private selectBatch(now: number): number[] {
    const due: { sub: Subscription; lateness: number }[] = [];
    for (const sub of this.subs.values()) {
      // Anything due within the tolerance counts as due now. Sleeping for two
      // milliseconds costs far more than two milliseconds -- host timers round
      // up, by as much as 15 ms on Windows and ~4 ms in a browser -- so a tiny
      // wait would burn more throughput than polling a hair early ever does.
      if (sub.suspended || sub.nextDue > now + DUE_TOLERANCE_MS) continue;
      due.push({ sub, lateness: (now - sub.nextDue) / Math.max(sub.intervalMs, 1) });
    }
    if (due.length === 0) return [];
    due.sort((a, b) => b.lateness - a.lateness);

    const first = due[0]?.sub;
    if (!first) return [];
    const firstDef = PID_BY_NUMBER.get(first.pid);

    // Anything with a variable-length payload has to go on its own, otherwise
    // we cannot tell where its data ends and the next PID's begins.
    if (!this.requester.canBatch || !firstDef?.batchable) return [first.pid];

    const batch: number[] = [first.pid];
    const ceiling = Math.min(this.requester.maxBatchSize, this.batchLimit);
    for (let i = 1; i < due.length && batch.length < ceiling; i++) {
      const cand = due[i]?.sub;
      if (!cand) continue;
      if (!PID_BY_NUMBER.get(cand.pid)?.batchable) continue;
      // Only grow the batch while the reply still fits what the adapter can
      // actually receive.
      if (!this.requester.responseFitsOneRequest([...batch, cand.pid])) continue;
      batch.push(cand.pid);
    }
    return batch;
  }

  private msUntilNextDue(now: number): number {
    let soonest = Infinity;
    for (const sub of this.subs.values()) {
      if (!sub.suspended && sub.nextDue < soonest) soonest = sub.nextDue;
    }
    if (soonest === Infinity) return 100;
    // The caller only reaches here when nothing was within DUE_TOLERANCE_MS, so
    // this can never spin; the floor just keeps the event loop breathing.
    return Math.max(DUE_TOLERANCE_MS, Math.min(soonest - now, 250));
  }

  private noteMiss(pid: number): void {
    const sub = this.subs.get(pid);
    if (!sub) return;
    sub.consecutiveMisses++;
    if (sub.consecutiveMisses >= this.missesBeforeSuspend) {
      sub.suspended = true;
    }
  }

  private noteTimeout(pid: number | undefined): void {
    const sub = pid === undefined ? undefined : this.subs.get(pid);
    if (!sub) return;
    sub.consecutiveTimeouts++;
    if (sub.consecutiveTimeouts >= this.timeoutsBeforeSuspend) {
      sub.suspended = true;
    }
  }

  private refreshStats(): void {
    const now = Date.now();
    const elapsed = (now - this.windowStart) / 1000;
    if (elapsed < 1) return;
    this.stats = {
      requestsPerSec: this.reqCount / elapsed,
      valuesPerSec: this.valCount / elapsed,
      latencyMs: this.latency,
      errorRate: this.reqCount > 0 ? this.errCount / this.reqCount : 0,
      activePids: this.activePids().length,
      batching: this.requester.canBatch,
    };
    this.reqCount = 0;
    this.valCount = 0;
    this.errCount = 0;
    this.windowStart = now;
  }
}

/**
 * A PID due within this many milliseconds is treated as due now, rather than
 * sleeping for a duration the host timer cannot honour accurately anyway.
 */
const DUE_TOLERANCE_MS = 4;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
